import { VehicleType } from "types/vehicle.type";
import { z } from "zod";

export enum WaterMeasurementLength {
  Stickprov = "Stickprov",
  SixHours = "SixHours",
  TwentyFourHours = "TwentyFourHours",
  Week = "Week",
  Other = "Other",
}

export enum WaterMeasurementPoint {
  InAvskiljare = "InAvskiljare",
  Provtagningsbrunn = "Provtagningsbrunn",
  AfterSupplementaryCleaning = "AfterSupplementaryCleaning",
  Other = "Other",
}

export enum WaterMeasurementReportingUnit {
  PerLiter = "PerLiter",
  PerVehicle = "PerVehicle",
}

export enum WaterMeasurementVehicleType {
  Car = "Car",
  Truck = "Truck",
  Train = "Train",
  Machines = "Machines",
  Mixed = "Mixed",
  Other = "Other",
}

export enum WaterMeterExists {
  Yes = "Yes",
  No = "No",
  Unknown = "Unknown",
}

export enum FacilityType {
  VehicleWash = "VehicleWash",
  Other = "Other",
}

export const waterMeasurementPointDataSchema = z.object({
  waterMeasurementLength: z
    .nativeEnum(WaterMeasurementLength)
    .nullable()
    .optional(),
  waterMeasurementLengthOther: z.string().nullable().optional(),
  measurementPoint: z.nativeEnum(WaterMeasurementPoint).nullable().optional(),
  measurementPointOther: z.string().nullable().optional(),
  analyses: z.string().nullable().optional(),
  limits: z.string().nullable().optional(),
  facilityType: z.nativeEnum(FacilityType).nullable().optional(),
  facilityTypeOther: z.string().nullable().optional(),
  waterMeterExists: z.nativeEnum(WaterMeterExists).nullable().optional(),
  reporting: z.nativeEnum(WaterMeasurementReportingUnit).nullable().optional(),
  vehicleType: z.nativeEnum(WaterMeasurementVehicleType).nullable().optional(),
  vehicleTypeOther: z.string().nullable().optional(),
});

export type WaterMeasurementPointData = z.infer<
  typeof waterMeasurementPointDataSchema
>;

export function getWaterMeasurementLengthLabel(
  value: WaterMeasurementLength | null | undefined,
): string {
  if (!value) return "";
  switch (value) {
    case WaterMeasurementLength.Stickprov:
      return "Stickprov";
    case WaterMeasurementLength.SixHours:
      return "6-timmars";
    case WaterMeasurementLength.TwentyFourHours:
      return "Dygnsprov";
    case WaterMeasurementLength.Week:
      return "Veckoprov";
    case WaterMeasurementLength.Other:
      return "Annat";
  }
}

export const getWaterMeasurementLengthValue = (
  data?: WaterMeasurementPointData,
) => {
  if (!data) return "";
  if (data.waterMeasurementLength === WaterMeasurementLength.Other)
    return data.waterMeasurementLengthOther;
  return getWaterMeasurementLengthLabel(data.waterMeasurementLength);
};

export const getWaterMeasurementPointValue = (
  data?: WaterMeasurementPointData,
) => {
  if (!data) return "";
  if (data.measurementPoint === WaterMeasurementPoint.Other)
    return data.measurementPointOther;
  return getWaterMeasurementPointLabel(data.measurementPoint);
};

export function getWaterMeasurementPointLabel(
  value: WaterMeasurementPoint | null | undefined,
): string {
  if (!value) return "";
  switch (value) {
    case WaterMeasurementPoint.InAvskiljare:
      return "I avskiljare";
    case WaterMeasurementPoint.Provtagningsbrunn:
      return "Provtagningsbrunn i eller efter avskiljare";
    case WaterMeasurementPoint.AfterSupplementaryCleaning:
      return "Efter kompletterande rening";
    case WaterMeasurementPoint.Other:
      return "Annan";
  }
}

export function getWaterMeterExistsLabel(
  value: WaterMeterExists | null | undefined,
): string {
  if (!value) return "";
  switch (value) {
    case WaterMeterExists.Yes:
      return "Finns";
    case WaterMeterExists.No:
      return "Finns ej";
    case WaterMeterExists.Unknown:
      return "Okänt";
  }
}

export function getWaterMeasurementReportingUnitLabel(
  value: WaterMeasurementReportingUnit | null | undefined,
): string {
  if (!value) return "";
  switch (value) {
    case WaterMeasurementReportingUnit.PerLiter:
      return "Halt per liter";
    case WaterMeasurementReportingUnit.PerVehicle:
      return "Halt per fordonsenhet";
  }
}

export function getWaterMeasurementVehicleTypeLabel(
  value: WaterMeasurementVehicleType | null | undefined,
): string {
  if (!value) return "";
  switch (value) {
    case WaterMeasurementVehicleType.Car:
      return "Personbilar";
    case WaterMeasurementVehicleType.Truck:
      return "Lastbilar";
    case WaterMeasurementVehicleType.Train:
      return "Tåg";
    case WaterMeasurementVehicleType.Machines:
      return "Maskiner";
    case WaterMeasurementVehicleType.Mixed:
      return "Blandat";
    case WaterMeasurementVehicleType.Other:
      return "Annat";
  }
}

export const getWaterMeasurementVehicleTypeValue = (
  data?: WaterMeasurementPointData,
) => {
  if (!data) return "";
  if (data.vehicleType === WaterMeasurementVehicleType.Other)
    return data.vehicleTypeOther;
  return getWaterMeasurementVehicleTypeLabel(data.vehicleType);
};

export const getFacilityTypeValue = (
  data?: WaterMeasurementPointData | null,
): string => {
  if (!data) return "";
  if (data.facilityType === FacilityType.VehicleWash) return "Fordonstvätt";
  if (data.facilityType === FacilityType.Other)
    return data.facilityTypeOther || "Annan";
  return "";
};

const hasText = (value: string | null | undefined): boolean =>
  !!value && value.trim() !== "";

/** Fält som krävs för att steg 1 i provtagningsprotokollet ska låsas upp. */
export type RequiredMeasurementDataField =
  | "waterMeasurementLength"
  | "measurementPoint"
  | "analyses"
  | "facilityType"
  | "waterMeterExists"
  | "reporting"
  | "vehicleType";

/**
 * Listar vilka förutsättningsfält (synkade mot avskiljaren) som saknar värde.
 * Ett "Annan/Annat"-val utan ifylld fritext räknas som saknat. Fordonstvätt-
 * fälten krävs bara när typ av verksamhet är Fordonstvätt.
 */
export const getMissingMeasurementDataFields = (
  data: WaterMeasurementPointData | null | undefined,
): RequiredMeasurementDataField[] => {
  const missing: RequiredMeasurementDataField[] = [];
  if (
    !data?.waterMeasurementLength ||
    (data.waterMeasurementLength === WaterMeasurementLength.Other &&
      !hasText(data.waterMeasurementLengthOther))
  )
    missing.push("waterMeasurementLength");
  if (
    !data?.measurementPoint ||
    (data.measurementPoint === WaterMeasurementPoint.Other &&
      !hasText(data.measurementPointOther))
  )
    missing.push("measurementPoint");
  if (!hasText(data?.analyses)) missing.push("analyses");
  if (
    !data?.facilityType ||
    (data.facilityType === FacilityType.Other &&
      !hasText(data.facilityTypeOther))
  )
    missing.push("facilityType");
  if (data?.facilityType === FacilityType.VehicleWash) {
    if (!data.waterMeterExists) missing.push("waterMeterExists");
    if (!data.reporting) missing.push("reporting");
    if (
      !data.vehicleType ||
      (data.vehicleType === WaterMeasurementVehicleType.Other &&
        !hasText(data.vehicleTypeOther))
    )
      missing.push("vehicleType");
  }
  return missing;
};

/**
 * Avgör om förutsättningsfälten (synkade mot avskiljaren) är kompletta,
 * vilket krävs för att steg 1 i provtagningsprotokollet ska låsas upp.
 */
export const isMeasurementDataComplete = (
  data: WaterMeasurementPointData | null | undefined,
): boolean => getMissingMeasurementDataFields(data).length === 0;
